import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import axiosInstance from '../../../services/axiosInstance'
import { isSuperAdmin, isAdmin, isAgent, isStudent } from '../../../utils/roleUtils'
import ConfirmationModal from '../../modals/ConfirmationModal'
import { 
  LayoutDashboard, 
  FileText, 
  Users,
  UserCheck,
  FolderOpen,
  Plane, 
  Briefcase, 
  MessageSquare,
  ClipboardList,
  BarChart3, 
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Bell,
  Cpu,
  Sparkles
} from 'lucide-react'
import './styles.scss'

const STAFF_NAV_ITEMS = [
  { path: '/dashboard',     label: 'Dashboard',            icon: LayoutDashboard },
  { path: '/applications',  label: 'Applications',         icon: FileText },
  { path: '/tasks',         label: 'Tasks & Reminders',    icon: ClipboardList },
  { path: '/analytics',     label: 'Reports & Analytics',  icon: BarChart3 },
  { path: '/ai-insights',   label: 'AI Insights',          icon: Sparkles },
  { path: '/ai-engine',     label: 'AI Engine',            icon: Cpu },
  { path: '/settings',      label: 'Settings',             icon: Settings },
]

const STUDENT_NAV_ITEMS = [
  { path: '/dashboard',     label: 'Dashboard',            icon: LayoutDashboard },
  { path: '/discover',      label: 'Discover Universities',icon: FolderOpen },
  { path: '/applications',  label: 'My Applications',      icon: FileText },
  { path: '/visa',          label: 'I-20 & Visa Tracking', icon: Plane },
  { path: '/messages',      label: 'Messages',             icon: MessageSquare },
]

export default function Sidebar({ isMobileOpen, setIsMobileOpen }) {
  const { user, isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    if (isAuthenticated && isStudent(user)) {
      fetchUnreadCount()
      // Optional: Poll every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000)
      return () => clearInterval(interval)
    }
  }, [isAuthenticated, user])

  const fetchUnreadCount = async () => {
    try {
      const res = await axiosInstance.get('/api/messages/unread-count')
      if (res.data.success) {
        setUnreadCount(res.data.data || 0)
      }
    } catch (e) {
      console.error('Failed to fetch unread count', e)
    }
  }

  // Hide sidebar for unauthenticated users
  if (!isAuthenticated) {
    return null
  }

  function handleLogout() {
    setShowLogoutModal(true)
  }

  function confirmLogout() {
    setShowLogoutModal(false)
    logout()
    navigate('/')
  }

  function cancelLogout() {
    setShowLogoutModal(false)
  }

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/')

  const baseNavItems = isStudent(user) ? STUDENT_NAV_ITEMS : STAFF_NAV_ITEMS
  
  const visibleNavItems = baseNavItems.filter(item => {
    if (item.path === '/settings' && !isStudent(user)) {
      return isAdmin(user)
    }
    return true
  })

  return (
    <>
    <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''} ${isMobileOpen ? 'sidebar--mobile-open' : ''}`}>
      {/* Brand */}
      <div className="sidebar__brand" onClick={() => navigate('/dashboard')}>
        <img src="/campusready_logo.svg" alt="CampusReady" className="sidebar__logo" />
        {!collapsed && <span className="sidebar__brand-text">CampusReady<span className="ai-suffix">.ai</span></span>}
      </div>

      {/* Navigation */}
      <nav className="sidebar__nav">
        {visibleNavItems.map(({ path, label, icon: Icon }) => {
          const isMessages = path === '/messages'
          return (
            <Link
              key={path}
              to={path}
              className={`sidebar__link ${isActive(path) ? 'sidebar__link--active' : ''}`}
            >
              <div style={{ position: 'relative' }}>
                <Icon size={20} />
                {isMessages && unreadCount > 0 && (
                  <span className="sidebar__badge-dot" />
                )}
              </div>
              {!collapsed && (
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                  {isMessages && unreadCount > 0 && (
                    <span className="sidebar__badge-count">{unreadCount}</span>
                  )}
                </div>
              )}
            </Link>
          )
        })}
      </nav>

      {/* Footer: User profile + collapse toggle */}
      <div className="sidebar__footer">
        <div 
          className="sidebar__user" 
          onClick={() => navigate(`/profile/${user?.id}`)} 
          style={{ cursor: 'pointer' }}
          title="View Profile"
        >
          <div className="sidebar__avatar">
            {(user?.fullName || user?.fullname || user?.username || 'U').charAt(0).toUpperCase()}
          </div>
          {!collapsed && (
            <div className="sidebar__user-info">
              <span className="sidebar__username">{user?.fullName || user?.fullname || user?.username || 'User'}</span>
              <span className="sidebar__role" style={{ fontSize: '0.65rem', opacity: 0.8, marginBottom: '2px' }}>{user?.email}</span>
              <span className="sidebar__role">
                {isSuperAdmin(user) ? 'Super Admin' : isAdmin(user) ? 'Admin' : isAgent(user) ? 'Agent' : isStudent(user) ? 'Student' : 'Admissions Manager'}
              </span>
            </div>
          )}
        </div>

        <div className="sidebar__actions">
          <button
            className="sidebar__action-btn sidebar__logout-btn"
            onClick={handleLogout}
            title="Sign Out"
          >
            <LogOut size={18} />
          </button>
          <button
            className="sidebar__action-btn sidebar__collapse-btn"
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
      </div>
    </aside>

      <ConfirmationModal
        isOpen={showLogoutModal}
        onClose={cancelLogout}
        onConfirm={confirmLogout}
        title="Sign Out"
        message="Are you sure you want to sign out?"
        confirmText="Sign Out"
        cancelText="Cancel"
      />
    </>
  )
}
